import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiEdit2, FiDownload, FiSend, FiPrinter, FiMail, FiCheckCircle, FiClock, FiFileText, FiUser, FiCalendar, FiDollarSign, FiLink } from 'react-icons/fi';
import Layout from '../components/Layout/Layout';
import api from '../services/api';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#374151'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  }
`;

const TitleSection = styled.div``;

const ContractNumber = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0;
`;

const ContractTitle = styled.p`
  font-size: 16px;
  color: ${props => props.theme?.colors?.textSecondary || '#666'};
  margin: 4px 0 0 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: ${props => props.theme?.colors?.primary || '#6366f1'};
    color: white;
    
    &:hover {
      background: ${props => props.theme?.colors?.primaryDark || '#4f46e5'};
    }
  }
  
  &.secondary {
    background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
    
    &:hover {
      background: ${props => props.theme?.colors?.border || '#e5e7eb'};
    }
  }
  
  &.success {
    background: #16a34a;
    color: white;
    
    &:hover {
      background: #15803d;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0;
`;

const CardBody = styled.div`
  padding: 20px;
`;

const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  border-radius: 12px;
  
  &.draft {
    background: #f3f4f6;
    color: #374151;
  }
  
  &.sent {
    background: #dbeafe;
    color: #1e40af;
  }
  
  &.viewed {
    background: #fef3c7;
    color: #92400e;
  }
  
  &.signed {
    background: #dcfce7;
    color: #166534;
  }
  
  &.expired {
    background: #fee2e2;
    color: #dc2626;
  }
  
  &.cancelled {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const StatusIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: rgba(255, 255, 255, 0.5);
`;

const StatusText = styled.div`
  flex: 1;
  
  .title {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 2px;
  }
  
  .subtitle {
    font-size: 14px;
    opacity: 0.8;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f9fafb'};
  border-radius: 8px;
`;

const InfoIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  color: ${props => props.theme?.colors?.primary || '#6366f1'};
`;

const InfoContent = styled.div`
  flex: 1;
  
  .label {
    font-size: 12px;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
    margin-bottom: 2px;
  }
  
  .value {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  }
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 24px;
  
  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${props => props.theme?.colors?.border || '#e0e0e0'};
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 20px;
  
  &:last-child {
    padding-bottom: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.completed ? '#16a34a' : props.theme?.colors?.border || '#e0e0e0'};
    border: 2px solid ${props => props.theme?.colors?.surface || '#ffffff'};
  }
`;

const TimelineContent = styled.div`
  .title {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  }
  
  .date {
    font-size: 12px;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
    margin-top: 2px;
  }
`;

const CustomerCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f9fafb'};
  border-radius: 8px;
`;

const CustomerAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${props => props.theme?.colors?.primary || '#6366f1'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
`;

const CustomerInfo = styled.div`
  flex: 1;
  
  .name {
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  }
  
  .email {
    font-size: 14px;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
  }
  
  .company {
    font-size: 13px;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
    margin-top: 4px;
  }
`;

const SignatureSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
`;

const SignatureItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f9fafb'};
  border-radius: 8px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SignatureInfo = styled.div`
  .name {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  }
  
  .type {
    font-size: 12px;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
    text-transform: capitalize;
  }
`;

const SignatureStatus = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  
  &.signed {
    color: #16a34a;
  }
  
  &.pending {
    color: #f59e0b;
  }
`;

const STATUS_MESSAGES = {
  draft: { title: 'Draft', subtitle: 'This contract has not been sent yet' },
  sent: { title: 'Sent', subtitle: 'Waiting for customer to view and sign' },
  viewed: { title: 'Viewed', subtitle: 'Customer has viewed the contract' },
  signed: { title: 'Signed', subtitle: 'All parties have signed the contract' },
  expired: { title: 'Expired', subtitle: 'This contract has expired' },
  cancelled: { title: 'Cancelled', subtitle: 'This contract was cancelled' },
};

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContract = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contracts/${id}`);
      setContract(response.data.contract);
    } catch (err) {
      setError('Failed to load contract');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const handleSend = async () => {
    if (!window.confirm('Send this contract to the customer?')) return;
    
    try {
      await api.post(`/contracts/${id}/send`);
      fetchContract();
    } catch (err) {
      alert('Failed to send contract');
      console.error(err);
    }
  };

  const handleDownload = async (format = 'docx') => {
    try {
      const response = await api.get(`/contracts/${id}/download?format=${format}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${contract.contract_number}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download contract');
      console.error(err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <p>Loading contract...</p>
        </Container>
      </Layout>
    );
  }

  if (error || !contract) {
    return (
      <Layout>
        <Container>
          <p style={{ color: 'red' }}>{error || 'Contract not found'}</p>
          <Button className="secondary" onClick={() => navigate('/contracts')}>
            <FiArrowLeft /> Back to Contracts
          </Button>
        </Container>
      </Layout>
    );
  }

  const statusInfo = STATUS_MESSAGES[contract.status] || STATUS_MESSAGES.draft;

  return (
    <Layout>
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/contracts')}>
              <FiArrowLeft />
            </BackButton>
            <TitleSection>
              <ContractNumber>{contract.contract_number}</ContractNumber>
              <ContractTitle>{contract.title}</ContractTitle>
            </TitleSection>
          </HeaderLeft>
          <ButtonGroup>
            {contract.status === 'draft' && (
              <>
                <Button className="secondary" onClick={() => navigate(`/contracts/${id}/edit`)}>
                  <FiEdit2 /> Edit
                </Button>
                <Button className="success" onClick={handleSend}>
                  <FiSend /> Send to Customer
                </Button>
              </>
            )}
            <Button className="secondary" onClick={() => handleDownload('docx')}>
              <FiDownload /> Download
            </Button>
            <Button className="secondary" onClick={() => handleDownload('pdf')}>
              <FiPrinter /> PDF
            </Button>
          </ButtonGroup>
        </Header>

        <StatusBanner className={contract.status}>
          <StatusIcon>
            {contract.status === 'signed' ? <FiCheckCircle /> : 
             contract.status === 'expired' ? <FiClock /> : <FiFileText />}
          </StatusIcon>
          <StatusText>
            <div className="title">{statusInfo.title}</div>
            <div className="subtitle">{statusInfo.subtitle}</div>
          </StatusText>
        </StatusBanner>

        <Grid>
          <div>
            <Card style={{ marginBottom: 24 }}>
              <CardHeader>
                <FiFileText />
                <CardTitle>Contract Details</CardTitle>
              </CardHeader>
              <CardBody>
                <InfoGrid>
                  <InfoItem>
                    <InfoIcon><FiCalendar /></InfoIcon>
                    <InfoContent>
                      <div className="label">Created</div>
                      <div className="value">{formatDate(contract.created_at)}</div>
                    </InfoContent>
                  </InfoItem>
                  
                  <InfoItem>
                    <InfoIcon><FiDollarSign /></InfoIcon>
                    <InfoContent>
                      <div className="label">Amount</div>
                      <div className="value">{formatCurrency(contract.amount, contract.currency_code)}</div>
                    </InfoContent>
                  </InfoItem>
                  
                  {contract.valid_from && (
                    <InfoItem>
                      <InfoIcon><FiCalendar /></InfoIcon>
                      <InfoContent>
                        <div className="label">Valid From</div>
                        <div className="value">{formatDate(contract.valid_from)}</div>
                      </InfoContent>
                    </InfoItem>
                  )}
                  
                  {contract.valid_until && (
                    <InfoItem>
                      <InfoIcon><FiCalendar /></InfoIcon>
                      <InfoContent>
                        <div className="label">Valid Until</div>
                        <div className="value">{formatDate(contract.valid_until)}</div>
                      </InfoContent>
                    </InfoItem>
                  )}
                  
                  {contract.template && (
                    <InfoItem>
                      <InfoIcon><FiFileText /></InfoIcon>
                      <InfoContent>
                        <div className="label">Template</div>
                        <div className="value">{contract.template.name}</div>
                      </InfoContent>
                    </InfoItem>
                  )}
                  
                  {contract.order && (
                    <InfoItem>
                      <InfoIcon><FiLink /></InfoIcon>
                      <InfoContent>
                        <div className="label">Order</div>
                        <div className="value">#{contract.order.order_number || contract.order.id}</div>
                      </InfoContent>
                    </InfoItem>
                  )}
                </InfoGrid>

                {contract.notes && (
                  <div style={{ marginTop: 20 }}>
                    <InfoContent>
                      <div className="label">Notes</div>
                      <div className="value" style={{ marginTop: 8 }}>{contract.notes}</div>
                    </InfoContent>
                  </div>
                )}
              </CardBody>
            </Card>

            {contract.customer && (
              <Card>
                <CardHeader>
                  <FiUser />
                  <CardTitle>Customer</CardTitle>
                </CardHeader>
                <CardBody>
                  <CustomerCard>
                    <CustomerAvatar>
                      {getInitials(contract.customer.name)}
                    </CustomerAvatar>
                    <CustomerInfo>
                      <div className="name">{contract.customer.name}</div>
                      {contract.customer.email && (
                        <div className="email">{contract.customer.email}</div>
                      )}
                      {contract.customer.company_name && (
                        <div className="company">{contract.customer.company_name}</div>
                      )}
                    </CustomerInfo>
                  </CustomerCard>

                  {contract.signatures && contract.signatures.length > 0 && (
                    <SignatureSection>
                      <CardTitle style={{ fontSize: 14, marginBottom: 12 }}>Signatures</CardTitle>
                      {contract.signatures.map((sig, i) => (
                        <SignatureItem key={i}>
                          <SignatureInfo>
                            <div className="name">{sig.signatory_name}</div>
                            <div className="type">{sig.signatory_type}</div>
                          </SignatureInfo>
                          <SignatureStatus className={sig.signed ? 'signed' : 'pending'}>
                            {sig.signed ? (
                              <>
                                <FiCheckCircle /> Signed
                              </>
                            ) : (
                              <>
                                <FiClock /> Pending
                              </>
                            )}
                          </SignatureStatus>
                        </SignatureItem>
                      ))}
                    </SignatureSection>
                  )}
                </CardBody>
              </Card>
            )}
          </div>

          <div>
            <Card>
              <CardHeader>
                <FiClock />
                <CardTitle>Activity</CardTitle>
              </CardHeader>
              <CardBody>
                <Timeline>
                  <TimelineItem completed>
                    <TimelineContent>
                      <div className="title">Contract Created</div>
                      <div className="date">{formatDate(contract.created_at)}</div>
                    </TimelineContent>
                  </TimelineItem>
                  
                  {contract.sent_at && (
                    <TimelineItem completed>
                      <TimelineContent>
                        <div className="title">Sent to Customer</div>
                        <div className="date">{formatDate(contract.sent_at)}</div>
                      </TimelineContent>
                    </TimelineItem>
                  )}
                  
                  {contract.viewed_at && (
                    <TimelineItem completed>
                      <TimelineContent>
                        <div className="title">Viewed by Customer</div>
                        <div className="date">{formatDate(contract.viewed_at)}</div>
                      </TimelineContent>
                    </TimelineItem>
                  )}
                  
                  {contract.signed_at && (
                    <TimelineItem completed>
                      <TimelineContent>
                        <div className="title">Contract Signed</div>
                        <div className="date">{formatDate(contract.signed_at)}</div>
                      </TimelineContent>
                    </TimelineItem>
                  )}
                  
                  {!contract.sent_at && contract.status === 'draft' && (
                    <TimelineItem>
                      <TimelineContent>
                        <div className="title">Awaiting Send</div>
                        <div className="date">Not sent yet</div>
                      </TimelineContent>
                    </TimelineItem>
                  )}
                </Timeline>
              </CardBody>
            </Card>
          </div>
        </Grid>
      </Container>
    </Layout>
  );
}
